import { Box, Button, Card, useTheme } from '@mui/material';
import { H5 } from 'components/Typography';
import Chart from 'react-apexcharts';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ProfessionSummary = ({ data }) => {
    const theme = useTheme();
    const [showAll, setShowAll] = useState(false);
    const { t } = useTranslation();

    const pairs = useMemo(() => {
        if (!data) return [];
        return Object.entries(data)
            .map(([name, value]) => ({
                name,
                value: Number(value) || 0,
            }))
            .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));
    }, [data]);

    const TOP = 9;
    const canExpand = pairs.length > TOP;
    const visiblePairs = useMemo(() => {
        if (showAll || !canExpand) return pairs;
        const head = pairs.slice(0, TOP);
        const restSum = pairs
            .slice(TOP)
            .reduce((acc, p) => acc + (Number(p.value) || 0), 0);
        if (restSum <= 0) return head;
        return [...head, { name: 'Others', value: restSum }];
    }, [pairs, showAll, canExpand]);

    const total = useMemo(
        () => pairs.reduce((acc, p) => acc + (isNaN(p.value) ? 0 : p.value), 0),
        [pairs],
    );

    const chartColors = useMemo(() => {
        const base = [
            theme.palette.primary.main,
            theme.palette.secondary.main,
            theme.palette.warning.main,
            theme.palette.info.main,
            theme.palette.success.main,
            theme.palette.error.main,
            theme.palette.primary.light,
            theme.palette.secondary.light,
            theme.palette.warning.light,
            theme.palette.info.light,
            theme.palette.success.light,
        ];
        return Array.from({ length: visiblePairs.length }, (_, idx) => base[idx % base.length]);
    }, [theme, visiblePairs.length]);

    const chartOptions = useMemo(() => {
        return {
            colors: chartColors,
            chart: {
                stacked: false,
                background: 'transparent',
                sparkline: {
                    enabled: true,
                },
                fontFamily: theme.typography.fontFamily,
                events: {
                    dataPointMouseEnter: (e) => {
                        if (e && e.target) e.target.style.cursor = 'pointer';
                    },
                    dataPointMouseLeave: (e) => {
                        if (e && e.target) e.target.style.cursor = 'default';
                    },
                },
            },
            plotOptions: {
                pie: {
                    donut: {
                        size: '60%',
                        labels: {
                            show: true,
                            total: {
                                show: true,
                                showAlways: true,
                                label: 'Total',
                                fontSize: '30px',
                                fontFamily: 'Helvetica, Arial, sans-serif',
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                formatter: function (w) {
                                    return w.globals.seriesTotals.reduce((a, b) => {
                                        return a + b;
                                    }, 0);
                                },
                            },
                        },
                    },
                },
            },
            states: {
                normal: {
                    filter: {
                        type: 'none',
                    },
                },
                hover: {
                    filter: {
                        type: 'none',
                    },
                },
                active: {
                    filter: {
                        type: 'none',
                    },
                },
            },
            labels: visiblePairs.map((p) => p.name),
            theme: {
                mode: theme.palette.mode,
            },
            legend: {
                show: true,
                position: 'bottom',
                fontSize: '13px',
                fontWeight: 500,
                itemMargin: {
                    horizontal: 10,
                },
                onItemClick: {
                    toggleDataSeries: false,
                },
                onItemHover: {
                    highlightDataSeries: false,
                },
                offsetY: 0,
            },
            tooltip: {
                theme: 'light',
                style: {
                    fontSize: '13px',
                },
                y: {
                    formatter: (val) => `${val} Participant`,
                },
                fillSeriesColor: false,
            },
            stroke: {
                width: 0,
            },
        };
    }, [chartColors, theme, visiblePairs]);

    return (
        <Card sx={{ padding: 3, height: '100%' }}>
            <H5 mb={3} textAlign="center">
                Profession Summary
            </H5>

            {visiblePairs.length > 0 && (
                <Chart
                    height={370}
                    type="donut"
                    options={chartOptions}
                    series={visiblePairs.map((p) => p.value)}
                />
            )}

            {visiblePairs.length === 0 && (
                <Box
                    sx={{
                        height: 370,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.palette.text.secondary,
                        fontWeight: 600,
                    }}
                >
                    {t('No data')}
                </Box>
            )}

            {canExpand && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={() => setShowAll((s) => !s)}
                    >
                        {showAll ? t('Show Top') : t('Show All')}
                    </Button>
                </Box>
            )}
        </Card>
    );
};

export default ProfessionSummary;
